import { apiClient } from './client';
import type { Vendor } from '@ixam/shared';

export interface BmcDto {
  type: 'idrac' | 'ilo' | 'xcc';
  host: string;
  username: string;
  password: string;
  firmwareVersion?: string | null;
  subscriptionId?: string | null;
}

export interface ServerHealthDto {
  status: 'ok' | 'warning' | 'critical' | 'unknown';
  lastEventAt: string | null;
  lastSeenAt: string | null;
}

export interface ServerDto {
  _id: string;
  name: string;
  vendor: Vendor;
  model: string;
  bmc: BmcDto;
  location: string | null;
  tags: string[];
  enabled: boolean;
  webhookSecret: string;
  webhookUrl: string;
  health: ServerHealthDto;
  createdAt: string;
  updatedAt: string;
}

export interface CreateServerInput {
  name: string;
  vendor: Vendor;
  model?: string;
  bmc: {
    type: 'idrac' | 'ilo' | 'xcc';
    host: string;
    username: string;
    password: string;
  };
  location?: string | null;
  tags?: string[];
  enabled?: boolean;
}

export type UpdateServerInput = Partial<CreateServerInput>;

export async function listServers(): Promise<ServerDto[]> {
  const res = await apiClient.get<{ items: ServerDto[] }>('/servers');
  return res.data.items;
}

export async function getServer(id: string): Promise<ServerDto> {
  const res = await apiClient.get<{ server: ServerDto }>(`/servers/${id}`);
  return res.data.server;
}

export async function createServer(input: CreateServerInput): Promise<ServerDto> {
  const res = await apiClient.post<{ server: ServerDto }>('/servers', input);
  return res.data.server;
}

export async function updateServer(id: string, input: UpdateServerInput): Promise<ServerDto> {
  const res = await apiClient.put<{ server: ServerDto }>(`/servers/${id}`, input);
  return res.data.server;
}

export async function deleteServer(id: string): Promise<void> {
  await apiClient.delete(`/servers/${id}`);
}

export interface SubscribeResult {
  result: { subscriptionId: string; destination: string; alreadyExisted: boolean };
}

export async function subscribeServer(id: string): Promise<SubscribeResult['result']> {
  const res = await apiClient.post<SubscribeResult>(`/servers/${id}/subscribe`);
  return res.data.result;
}

export interface TestEventResult {
  result: { ok: boolean; error?: string | null };
}

export async function sendServerTestEvent(id: string): Promise<TestEventResult['result']> {
  const res = await apiClient.post<TestEventResult>(`/servers/${id}/test-event`);
  return res.data.result;
}

export interface RefreshHealthResult {
  status: 'ok' | 'warning' | 'critical' | 'unknown';
  raw: string | null;
}

export async function refreshServerHealth(id: string): Promise<{
  server: ServerDto;
  result: RefreshHealthResult;
}> {
  const res = await apiClient.post<{ server: ServerDto; result: RefreshHealthResult }>(
    `/servers/${id}/refresh-health`,
  );
  return res.data;
}
